<template>
  <div class="cart-item">
    <!-- Product Image -->
    <div class="cart-item-image">
      <img :src="imageUrl" :alt="item.product_name">
    </div>

    <!-- Product Details -->
    <div class="cart-item-info">
      <h3 class="cart-item-name">
        <router-link :to="`/product/${item.slug}`">
          {{ item.product_name }}
        </router-link>
      </h3>
      <p v-if="variantInfo" class="cart-item-variant">
        {{ variantInfo }}
      </p>
      <div class="cart-item-price">
        {{ formatPrice(item.price) }}
      </div>
    </div>

    <!-- Quantity Controls -->
    <div class="cart-item-quantity">
      <div class="quantity-selector">
        <button 
          @click="$emit('update-quantity', item.id, item.quantity - 1)" 
          class="qty-btn"
          :disabled="item.quantity <= 1 || loading"
        >-</button>
        <span class="qty-value">{{ item.quantity }}</span>
        <button 
          @click="$emit('update-quantity', item.id, item.quantity + 1)" 
          class="qty-btn"
          :disabled="loading"
        >+</button>
      </div>
    </div>

    <!-- Total & Remove -->
    <div class="cart-item-total">
      <div class="total-price">{{ formatPrice(item.total_price) }}</div>
      <button 
        @click="$emit('remove-item', item.id)" 
        class="remove-btn"
        :disabled="loading"
      >
        ลบ
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { getImageUrl } from '../utils/imageUrl';

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
});

defineEmits(['update-quantity', 'remove-item']);

const imageUrl = computed(() => {
  return getImageUrl(props.item.image) || 'https://placehold.co/100x100?text=Product';
});

const variantInfo = computed(() => {
  if (props.item.variant_options) {
    const parts = [];
    if (props.item.variant_options.color) parts.push(props.item.variant_options.color);
    if (props.item.variant_options.size) parts.push(props.item.variant_options.size);
    return parts.join(' / ');
  }
  return null;
});

const formatPrice = (price) => {
  const num = parseFloat(price) || 0;
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(num);
};
</script>

<style scoped>
.cart-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid var(--color-border-light);
}

.cart-item:last-child {
  border-bottom: none;
}

.cart-item-image {
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #f9fafb;
}

.cart-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cart-item-info {
  flex: 1;
  min-width: 0;
}

.cart-item-name {
  font-weight: 500;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.cart-item-name a {
  color: inherit;
  text-decoration: none;
}

.cart-item-name a:hover {
  color: var(--color-primary);
}

.cart-item-variant {
  font-size: 0.875rem;
  color: var(--color-text-muted);
}

.cart-item-price {
  font-weight: 600;
  color: var(--color-text);
  margin-top: 0.25rem;
}

.cart-item-quantity {
  flex-shrink: 0;
}

.quantity-selector {
  display: flex;
  align-items: center;
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  overflow: hidden;
}

.qty-btn {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  border: none;
  cursor: pointer;
  color: var(--color-text);
}

.qty-btn:hover:not(:disabled) {
  background: #f3f4f6;
}

.qty-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.qty-value {
  width: 40px;
  text-align: center;
  font-weight: 500;
  font-size: 0.875rem;
  border-left: 1px solid var(--color-border);
  border-right: 1px solid var(--color-border);
}

.cart-item-total {
  text-align: right;
  flex-shrink: 0;
  min-width: 80px;
}

.total-price {
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 0.5rem;
}

.remove-btn {
  font-size: 0.875rem;
  color: #dc2626;
  background: none;
  border: none;
  cursor: pointer;
}

.remove-btn:hover {
  text-decoration: underline;
}

.remove-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Mobile responsive */
@media (max-width: 640px) {
  .cart-item {
    flex-wrap: wrap;
  }
  
  .cart-item-info {
    flex: 1 1 calc(100% - 96px);
  }
  
  .cart-item-quantity,
  .cart-item-total {
    flex: 1;
  }
  
  .cart-item-total {
    text-align: right;
  }
}
</style>
